import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, CheckCircle2, Phone, Users, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function toISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

const CASE_FOR_OPTIONS = [
  { value: "self", label: "Myself" },
  { value: "incarcerated", label: "An incarcerated loved one" },
  { value: "family", label: "Another family member" },
  { value: "attorney", label: "I'm an attorney inquiring" },
  { value: "other", label: "Other" },
];

const URGENCY_OPTIONS = [
  { value: "low", label: "Low — general inquiry" },
  { value: "medium", label: "Medium — action needed soon" },
  { value: "high", label: "High — time-sensitive matter" },
];

// Returns the Tailwind classes for a time-slot button based on its state.
function getSlotClasses(isSelected, isAvailable) {
  if (isSelected) return "border-amber-400 bg-blue-700 text-white";
  if (isAvailable) return "border-zinc-800 text-zinc-200 hover:border-amber-400 hover:text-amber-400";
  return "border-zinc-900 text-zinc-600 cursor-not-allowed line-through";
}

export default function Booking() {
  const [step, setStep] = useState(1); // 1: intake, 2: date/time, 3: confirm
  const [intake, setIntake] = useState({
    name: "", email: "", phone: "",
    case_for: "self", urgency: "medium", situation: "",
  });
  const [appointmentType, setAppointmentType] = useState("consultation");
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    api.get("/bookings/availability", { params: { date: toISODate(date) } })
      .then((r) => setSlots(r.data.slots))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const validateIntake = () => {
    if (!intake.name.trim()) return "Please enter your name.";
    if (!intake.email.trim()) return "Please enter your email.";
    if (!intake.phone.trim()) return "Please enter your phone.";
    if (!intake.situation.trim()) return "Please share a brief description of your situation.";
    return null;
  };

  const goToScheduling = () => {
    const err = validateIntake();
    if (err) return toast.error(err);
    setStep(2);
  };

  const submit = async () => {
    if (!date || !selectedSlot) return toast.error("Please select a date and time slot");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...intake,
        appointment_type: appointmentType,
        date: toISODate(date),
        time_slot: selectedSlot,
        notes: intake.situation,
      });
      setConfirmation(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 3 && confirmation) {
    return (
      <div className="container-pl py-24" data-testid="booking-confirmation">
        <div className="max-w-2xl mx-auto border border-zinc-800 rounded-sm p-14 text-center bg-zinc-950">
          <CheckCircle2 className="h-12 w-12 text-amber-400 mx-auto mb-8" strokeWidth={1.25} />
          <p className="overline mb-4">Confirmed</p>
          <h1 className="display-serif text-4xl text-zinc-50 mb-4">Your appointment is booked.</h1>
          <div className="text-left bg-black border border-zinc-800 rounded-sm p-6 my-8">
            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <span className="text-zinc-400">Type</span>
              <span className="font-medium capitalize">{confirmation.appointment_type === "phone" ? "Phone Call" : "Consultation"}</span>
              <span className="text-zinc-400">Date</span>
              <span className="font-medium">{confirmation.date}</span>
              <span className="text-zinc-400">Time</span>
              <span className="font-medium">{confirmation.time_slot}</span>
              <span className="text-zinc-400">Confirmation</span>
              <span className="font-mono text-xs">{confirmation.id}</span>
            </div>
          </div>
          <p className="text-sm text-zinc-400 mb-8 max-w-md mx-auto">
            We've recorded your appointment. We'll reach out using the phone and email you provided.
          </p>
          <Button
            onClick={() => {
              setConfirmation(null);
              setStep(1);
              setDate(undefined);
              setSelectedSlot(null);
              setIntake({ name: "", email: "", phone: "", case_for: "self", urgency: "medium", situation: "" });
            }}
            variant="outline"
            className="rounded-sm border-amber-400 text-amber-400"
            data-testid="book-another-btn"
          >
            Book another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page">
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="container-pl py-16 lg:py-20">
          <p className="overline mb-5">Booking</p>
          <h1 className="display-serif text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-4">
            Schedule a Consultation
          </h1>
          <p className="text-base lg:text-lg text-zinc-300 max-w-xl font-light">
            A brief intake, then choose your time. Confidential and free.
          </p>

          {/* Step indicator */}
          <div className="flex items-center gap-4 mt-10" data-testid="step-indicator">
            {[
              { n: 1, label: "Intake" },
              { n: 2, label: "Date & Time" },
              { n: 3, label: "Confirmation" },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex items-center gap-3 flex-1 last:flex-none">
                <div
                  className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs font-mono ${
                    step >= s.n ? "border-amber-400 bg-blue-700 text-white" : "border-zinc-700 text-zinc-500"
                  }`}
                >
                  {s.n}
                </div>
                <p className={`text-sm hidden sm:block ${step >= s.n ? "text-zinc-50 font-medium" : "text-zinc-500"}`}>
                  {s.label}
                </p>
                {i < arr.length - 1 && <div className={`h-px flex-1 ${step > s.n ? "bg-blue-700" : "bg-zinc-800"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-pl py-14">
        {step === 1 && (
          <div className="max-w-2xl" data-testid="intake-step">
            <h2 className="display-serif text-3xl text-zinc-50 mb-2">Tell us about your situation.</h2>
            <p className="text-sm text-zinc-400 mb-10">
              Brief, confidential intake. We'll review before your appointment so the call is focused and useful.
            </p>

            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={intake.name} onChange={(e) => setIntake({ ...intake, name: e.target.value })} className="rounded-sm mt-1.5 h-11" data-testid="booking-name" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={intake.phone} onChange={(e) => setIntake({ ...intake, phone: e.target.value })} className="rounded-sm mt-1.5 h-11" data-testid="booking-phone" />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={intake.email} onChange={(e) => setIntake({ ...intake, email: e.target.value })} className="rounded-sm mt-1.5 h-11" data-testid="booking-email" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Who is the case for?</Label>
                  <Select value={intake.case_for} onValueChange={(v) => setIntake({ ...intake, case_for: v })}>
                    <SelectTrigger className="rounded-sm mt-1.5 h-11" data-testid="booking-case-for"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CASE_FOR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Urgency level</Label>
                  <Select value={intake.urgency} onValueChange={(v) => setIntake({ ...intake, urgency: v })}>
                    <SelectTrigger className="rounded-sm mt-1.5 h-11" data-testid="booking-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="situation">Situation details</Label>
                <Textarea
                  id="situation"
                  value={intake.situation}
                  onChange={(e) => setIntake({ ...intake, situation: e.target.value })}
                  className="rounded-sm mt-1.5 min-h-[160px]"
                  placeholder="A few sentences about your situation — what's happening, what you need, any deadlines we should know about."
                  data-testid="booking-situation"
                />
                <p className="text-xs text-zinc-500 mt-2">Treated as strictly confidential.</p>
              </div>

              <div className="pt-4 flex justify-end">
                <Button onClick={goToScheduling} className="rounded-sm bg-blue-700 hover:bg-blue-600 h-12 px-8" data-testid="booking-continue">
                  Continue to Scheduling
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="scheduling-step">
            <Tabs value={appointmentType} onValueChange={setAppointmentType} className="mb-10">
              <TabsList className="grid w-full max-w-md grid-cols-2 rounded-sm" data-testid="appointment-type-tabs">
                <TabsTrigger value="consultation" className="rounded-sm" data-testid="tab-consultation">
                  <Users className="h-4 w-4 mr-2" strokeWidth={1.5} /> Consultation
                </TabsTrigger>
                <TabsTrigger value="phone" className="rounded-sm" data-testid="tab-phone">
                  <Phone className="h-4 w-4 mr-2" strokeWidth={1.5} /> Phone Call
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="border border-zinc-800 rounded-sm p-6 bg-zinc-950">
                <h3 className="text-base font-medium text-zinc-50 mb-1">Select a date</h3>
                <p className="text-xs text-zinc-400 mb-4">Weekdays only.</p>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    return d < today || d.getDay() === 0 || d.getDay() === 6;
                  }}
                  className="rounded-sm border border-zinc-800 p-0"
                  data-testid="booking-calendar"
                />
              </div>

              <div className="border border-zinc-800 rounded-sm p-6 bg-zinc-950">
                <h3 className="text-base font-medium text-zinc-50 mb-3">Select a time</h3>
                {!date && <p className="text-sm text-zinc-400">Pick a date first.</p>}
                {date && loadingSlots && <Loader2 className="h-5 w-5 animate-spin text-amber-400" />}
                {date && !loadingSlots && (
                  <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                    {slots.map((s) => (
                      <button
                        key={s.time}
                        onClick={() => setSelectedSlot(s.time)}
                        disabled={!s.available}
                        className={`text-xs py-2.5 px-2 border rounded-sm transition-colors ${getSlotClasses(selectedSlot === s.time, s.available)}`}
                        data-testid={`slot-${s.time.replace(/[: ]/g, "")}`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-zinc-800 space-y-3 text-sm">
                  <p className="overline">Your intake</p>
                  <div className="space-y-1.5 text-zinc-300">
                    <p><span className="text-zinc-400">Name:</span> {intake.name}</p>
                    <p><span className="text-zinc-400">For:</span> {CASE_FOR_OPTIONS.find(o => o.value === intake.case_for)?.label}</p>
                    <p><span className="text-zinc-400">Urgency:</span> {URGENCY_OPTIONS.find(o => o.value === intake.urgency)?.label}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-8">
              <Button variant="ghost" onClick={() => setStep(1)} className="rounded-sm" data-testid="back-to-intake-btn">
                <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} /> Back to intake
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !date || !selectedSlot}
                className="rounded-sm bg-blue-700 hover:bg-blue-600 h-12 px-8"
                data-testid="confirm-booking-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm Booking
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
